import { getActiveLanguage } from '@carlito/api';
import { ListAvailableTimeslotsResponse, ValidTimeSlot } from '@carlito/bookito-client';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { IoChevronUpSharp } from 'react-icons/io5';
import { BookitoTimeSlotChip } from '../BookitoTimeSlotChip';

import { SlUserFemale as FemaleIcon, SlUser as MaleIcon } from 'react-icons/sl';

type Props = {
  showMoreThreshold: number;
  timeslotsData: ListAvailableTimeslotsResponse;
  bookedForDate?: Date;
  onSelectTimeSlot: (params: ValidTimeSlot) => void;
  maxParticipants?: number | null;
  duration: number;
  selectedEmployeeId?: string;
};

type GroupedEmployeeTimeslots = {
  employee?: NonNullable<ListAvailableTimeslotsResponse['employees']>[number];
  timeSlots: ValidTimeSlot[];
}[];
function groupTimeSlotsByEmployees({
  availableTimeSlots,
  employees,
}: ListAvailableTimeslotsResponse): GroupedEmployeeTimeslots {
  return employees && employees.length > 0
    ? employees.map((employee) => {
        const timeSlots = availableTimeSlots.filter((slot) => slot.employeeId === employee.id);
        return { employee, timeSlots };
      })
    : [{ employee: undefined, timeSlots: availableTimeSlots }];
}

function BookingTimeSlotList({
  onSelectTimeSlot,
  maxParticipants,
  showMoreThreshold,
  bookedForDate,
  timeslotsData,
  duration,
  selectedEmployeeId,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  const groupedTimeSlots = useMemo(() => groupTimeSlotsByEmployees(timeslotsData), [timeslotsData]);

  function renderList(items: ListAvailableTimeslotsResponse['availableTimeSlots']) {
    let endTimeKey = '';
    return (
      <div className={'grid grid-cols-2 gap-x-3  sm:gap-x-5'}>
        {items.map((currentTimeslot) => {
          const { startDateTime, numOfConfirmedParticipants } = currentTimeslot;
          const startTime = new Date(startDateTime);

          if (duration) {
            endTimeKey = new Date(startTime.getTime() + duration).toLocaleTimeString(
              getActiveLanguage(),
              {
                hour: '2-digit',
                minute: '2-digit',
              }
            );
          }

          const startTimeKey = startTime.toLocaleTimeString(getActiveLanguage(), {
            hour: '2-digit',
            minute: '2-digit',
          });

          const timeslotDisplayKey =
            duration > 0 ? `${startTimeKey} - ${endTimeKey}` : startTimeKey;

          const isSelected =
            bookedForDate?.toISOString() === startDateTime &&
            (selectedEmployeeId ? selectedEmployeeId === currentTimeslot.employeeId : true);
          return (
            <BookitoTimeSlotChip
              maxParticipants={maxParticipants}
              isSelected={isSelected}
              key={startDateTime.toString()}
              value={timeslotDisplayKey}
              numOfConfirmedParticipants={numOfConfirmedParticipants}
              onSelect={() => onSelectTimeSlot(currentTimeslot)}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-14 mt-14">
      {groupedTimeSlots.map(({ timeSlots, employee }, idx) => {
        const slotsToShow = timeSlots.slice(0, showMoreThreshold);
        const remainingSlots = timeSlots.slice(showMoreThreshold, timeSlots.length);

        return (
          <div key={idx}>
            {employee ? (
              <div className="my-4 flex items-center gap-4">
                {employee.gender === 'female' && <FemaleIcon className="text-pink-500" />}
                {employee.gender === 'male' && <MaleIcon className="text-blue-500" />}
                <h2 className="text-xl">
                  {employee.firstName} {employee.lastName}
                </h2>
              </div>
            ) : null}

            {slotsToShow.length > 0 && renderList(slotsToShow)}
            {remainingSlots.length > 0 && (
              <>
                <div
                  className={`transition-all duration-300 ease-in-out ${
                    isExpanded ? 'opacity-1' : 'max-h-0 overflow-hidden opacity-0'
                  }`}
                >
                  <div>{renderList(remainingSlots)}</div>
                </div>
                <div className={'flex justify-center'}>
                  <button
                    type="button"
                    className={'flex gap-x-2 text-sm items-center'}
                    onClick={() => {
                      setIsExpanded((prevState) => !prevState);
                    }}
                  >
                    <span> {isExpanded ? <Trans>show-less</Trans> : <Trans>show-more</Trans>}</span>
                    <IoChevronUpSharp className={cn(isExpanded ? '' : 'rotate-180')} />
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default BookingTimeSlotList;
